import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import PageTitle from '../fragments/PageTitle'
import Button from '../common/Button'
import DatePickerWithLabelRanged from '../common/interactive/DatePickerWithLabelRanged'
import { getReleaseNotes } from '../../lib/fetchApi'
import classes from './ReleaseNotes.module.css'

function formatReleaseDate(dateStr) {
    if (!dateStr) {
        return ''
    }
    const date = new Date(`${dateStr}T00:00:00`)
    if (Number.isNaN(date.getTime())) {
        return dateStr
    }
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}

function formatShortDateParts(dateStr) {
    if (!dateStr) {
        return { month: '', day: '' }
    }
    const date = new Date(`${dateStr}T00:00:00`)
    if (Number.isNaN(date.getTime())) {
        return { month: '', day: dateStr }
    }
    return {
        month: date.toLocaleDateString(undefined, { month: 'short' }).toUpperCase(),
        day: String(date.getDate()),
    }
}

export default function ReleaseNotes() {
    const token = useSelector(state => state.user.token)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [payload, setPayload] = useState(null)
    const requestIdRef = useRef(0)

    function handleDateFilterAction(actionPayload) {
        const value = actionPayload?.data?.value

        if (!value) {
            setStartDate('')
            setEndDate('')
            setPage(1)
            return
        }

        if (typeof value === 'string' && value.includes('|')) {
            const parts = value.split('|')
            setStartDate(parts[0] ?? '')
            setEndDate(parts[1] ?? '')
        } else {
            setStartDate(typeof value === 'string' ? value : '')
            setEndDate('')
        }

        setPage(1)
    }

    const preSelectedValue = startDate ? (endDate ? `${startDate}|${endDate}` : startDate) : null

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim())
            setPage(1)
        }, 350)
        return () => clearTimeout(timer)
    }, [searchInput])

    const loadNotes = useCallback(async () => {
        if (!token) {
            return
        }
        const requestId = ++requestIdRef.current
        setLoading(true)
        setError(null)
        try {
            const data = await getReleaseNotes(token, search, page, startDate, endDate)
            if (requestId !== requestIdRef.current) {
                return
            }
            if (data?.errorMessage) {
                setError(data.errorMessage)
                setPayload(null)
            } else {
                setPayload(data)
            }
        } catch (e) {
            if (requestId !== requestIdRef.current) {
                return
            }
            setError('Unable to load release notes.')
            setPayload(null)
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false)
            }
        }
    }, [token, search, page, startDate, endDate])

    useEffect(() => {
        loadNotes()
    }, [loadNotes])

    const notes = payload?.data ?? []
    const meta = payload?.meta
    const currentPage = meta?.current_page ?? page
    const lastPage = meta?.last_page ?? 1
    const hasPrev = currentPage > 1
    const hasNext = currentPage < lastPage

    return (
        <div className={classes.page}>
            <div className={classes.header_box}>
                <PageTitle isContainer={true}>Release Notes</PageTitle>
                <p className={classes.subtitle}>
                    See what changed in myeCM. Newest updates appear first.
                </p>
            </div>

            <div className={classes.panel}>
                <div className={classes.panel_toolbar}>
                    <div className={classes.search_wrap}>
                        <i className={`ti ti-search ${classes.search_icon}`} aria-hidden="true"></i>
                        <input
                            type="search"
                            className={classes.search_input}
                            placeholder="Search by title or keyword..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            aria-label="Search release notes"
                        />
                    </div>
                    <div className={classes.date_filters}>
                        <DatePickerWithLabelRanged
                            label={'Release Date'}
                            action={handleDateFilterAction}
                            identifier={{ type: 'release_date', label: 'Release Date' }}
                            preSelectedValue={preSelectedValue}
                        />
                    </div>
                    {meta?.total != null && !loading && !error && (
                        <span className={classes.result_count}>
                            {meta.total} {meta.total === 1 ? 'note' : 'notes'}
                        </span>
                    )}
                </div>

                <div className={classes.panel_body}>
                    {loading && (
                        <div className={classes.empty_state}>
                            <span className={classes.empty_spinner} aria-hidden="true"></span>
                            <p className={classes.empty_title}>Loading release notes...</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className={`${classes.empty_state} ${classes.empty_error}`}>
                            <i className="ti ti-alert-circle" aria-hidden="true"></i>
                            <p className={classes.empty_title}>{error}</p>
                        </div>
                    )}

                    {!loading && !error && notes.length === 0 && (
                        <div className={classes.empty_state}>
                            <i className="ti ti-clipboard-list" aria-hidden="true"></i>
                            <p className={classes.empty_title}>
                                {search ? 'No matching release notes' : 'No release notes yet'}
                            </p>
                            <p className={classes.empty_text}>
                                {search
                                    ? 'Try a different keyword, or clear the search to see all notes.'
                                    : 'When an admin publishes a release note, it will show up here.'}
                            </p>
                        </div>
                    )}

                    {!loading && !error && notes.length > 0 && (
                        <div className={classes.notes_list}>
                            {notes.map((note) => {
                                const dateParts = formatShortDateParts(note.release_date)
                                return (
                                    <article key={note.id} className={classes.note_card}>
                                        <div className={classes.date_badge} aria-hidden="true">
                                            <span className={classes.date_month}>{dateParts.month}</span>
                                            <span className={classes.date_day}>{dateParts.day}</span>
                                        </div>
                                        <div className={classes.note_content}>
                                            <header className={classes.note_header}>
                                                <h2 className={classes.note_title}>{note.title}</h2>
                                                <time className={classes.note_date} dateTime={note.release_date}>
                                                    {formatReleaseDate(note.release_date)}
                                                </time>
                                            </header>
                                            <div
                                                className={classes.note_body}
                                                dangerouslySetInnerHTML={{ __html: note.body || '' }}
                                            />
                                        </div>
                                    </article>
                                )
                            })}
                        </div>
                    )}
                </div>

                {meta && lastPage > 1 && !loading && !error && notes.length > 0 && (
                    <div className={classes.pagination}>
                        <span className={classes.pagination_info}>
                            Page {currentPage} of {lastPage}
                        </span>
                        <div className={classes.pagination_actions}>
                            <Button
                                btnStyle="btn_light_grey"
                                isDisabled={!hasPrev || loading}
                                click={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Previous
                            </Button>
                            <Button
                                btnStyle="btn_light_grey"
                                isDisabled={!hasNext || loading}
                                click={() => setPage((p) => p + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
